import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ANALYTICS_EVENT } from '@/common/constants/analyticsEvents';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { useAnalytics } from '@/common/contexts/AnalyticsContext';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { login, register } from '@/api/query/auth';
import api from '@/api/api';
import storageManager from '@/api/storageManager';
import { checkRegistrationPassword } from '@/common/functions/checkPassword';
import { getRegistrationUtms } from '@/common/functions/getUtms';
import {
	AUTH_COMPLETE_DELAY_MS,
	AUTH_SUBMIT_STATE,
	AUTH_SUCCESS_DELAY_MS,
} from '../constants/authForm.constants';

export const useAuthForm = ({
	mode,
	onSuccess,
	translations,
}) => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState(null);
	const [authSubmitState, setAuthSubmitState] = useState(
		AUTH_SUBMIT_STATE.IDLE,
	);

	const successTimeoutRef = useRef(null);
	const completeTimeoutRef = useRef(null);
	const previousModeRef = useRef(mode);

	const { lang } = useContext(LanguageContext);
	const { trackAnalytics } = useAnalytics();
	const {
		authRegistrationSource,
		authReturnPath,
		authPrefilledEmail,
		refreshAuthState,
	} = useGuestAuth();

	useEffect(() => {
		return () => {
			clearTimeout(successTimeoutRef.current);
			clearTimeout(completeTimeoutRef.current);
		};
	}, []);

	useEffect(() => {
		const emailParam =
			authPrefilledEmail ||
			new URLSearchParams(window.location.search).get('email');

		if (!emailParam) {
			return;
		}

		setEmail(emailParam);
	}, [authPrefilledEmail]);

	useEffect(() => {
		if (previousModeRef.current === mode) {
			return;
		}

		previousModeRef.current = mode;
		setPassword('');
		setConfirmPassword('');
		setError(null);
		setAuthSubmitState(AUTH_SUBMIT_STATE.IDLE);
		clearTimeout(successTimeoutRef.current);
		clearTimeout(completeTimeoutRef.current);
	}, [mode]);

	const clearAuthSuccessTimers = () => {
		clearTimeout(successTimeoutRef.current);
		clearTimeout(completeTimeoutRef.current);
	};

	const scheduleAuthSuccess = (isRegistration) => {
		setAuthSubmitState(AUTH_SUBMIT_STATE.PENDING);

		successTimeoutRef.current = setTimeout(() => {
			setAuthSubmitState(AUTH_SUBMIT_STATE.SUCCESS);

			completeTimeoutRef.current = setTimeout(() => {
				onSuccess?.({ isRegistration });
			}, AUTH_COMPLETE_DELAY_MS);
		}, AUTH_SUCCESS_DELAY_MS);
	};

	const persistAuthSession = ({ data }) => {
		storageManager.auth.setToken(data.token, api.axios);
		storageManager.auth.setRefreshToken(data.refreshToken);
		void refreshAuthState();
	};

	const handleAuthSuccess = ({ data }) => {
		persistAuthSession({ data });
		trackAnalytics(ANALYTICS_EVENT.AUTH.LOGIN, { method: 'login' });
		scheduleAuthSuccess(false);
	};

	const loginMutation = useMutation({
		mutationFn: (payload) => login(payload),
		onSuccess: handleAuthSuccess,
		onError: () => {
			clearAuthSuccessTimers();
			setAuthSubmitState(AUTH_SUBMIT_STATE.IDLE);
			setError(
				translations.invalidCredentials || 'Invalid credentials',
			);
		},
	});

	const registerMutation = useMutation({
		mutationFn: (payload) => register(payload),
		onSuccess: ({ data }) => {
			persistAuthSession({ data });
			trackAnalytics(ANALYTICS_EVENT.AUTH.LOGIN, { method: 'register' });
			scheduleAuthSuccess(data.is_new_customer === true);
		},
		onError: () => {
			clearAuthSuccessTimers();
			setAuthSubmitState(AUTH_SUBMIT_STATE.IDLE);
			setError(
				translations.registerFailed ||
					'Check your email — an account may already exist.',
			);
		},
	});

	const normalizedEmail = email?.toLowerCase()?.replace(/\s/g, '');
	const isLogin = mode === 'login';

	const handleSubmit = (event) => {
		event.preventDefault();
		setError(null);

		if (isLogin) {
			loginMutation.mutate({ email: normalizedEmail, password });
			return;
		}

		const passwordValidation = checkRegistrationPassword(
			password,
			confirmPassword,
		);

		if (!passwordValidation.valid) {
			setError(
				translations[passwordValidation.messageKey] ||
					passwordValidation.messageKey,
			);
			return;
		}

		registerMutation.mutate({
			email: normalizedEmail,
			password,
			lang,
			gainUtms: getRegistrationUtms(authRegistrationSource, authReturnPath),
		});
	};

	const isAuthMutationLoading =
		loginMutation.isLoading || registerMutation.isLoading;
	const isAuthPending =
		isAuthMutationLoading || authSubmitState === AUTH_SUBMIT_STATE.PENDING;
	const isAuthSuccess = authSubmitState === AUTH_SUBMIT_STATE.SUCCESS;
	const isSubmitDisabled = isAuthPending || isAuthSuccess;

	return {
		email,
		setEmail,
		password,
		setPassword,
		confirmPassword,
		setConfirmPassword,
		error,
		normalizedEmail,
		handleSubmit,
		isAuthPending,
		isAuthSuccess,
		isSubmitDisabled,
	};
};
